import { useQuerySubscription } from "hooks/common";
import { getGQL } from "utils/utils";
import { TSubscriptionUser } from "admin-app/models";
import { useMemo, useRef, useState } from "react";
import { ColDef } from "ag-grid-enterprise";
import CiqAgGridUtil from "admin-app/components/ag-grid-util";
import { USERS_OF_SUBSCRIPTION } from "admin-app/services/ciq-queries";
import CiqAgSearch from "components/ciq-ag-search";
import { AgGridReact } from "ag-grid-react";
import { comparatorWithEmptyAtBottom } from "utils/grid-utils";
import { getGridStatusBudge } from "components/ciq-log-page-header/grid-status-budge";
import { calculateLogPageStats } from "components/ciq-log-page-header";
import {
  dummyColumnDef,
  truncatedTextCellRenderer
} from "components/cell-renders";
import { DateUtils } from "utils/dateutils";
import {
  StatusCellRenderer,
  UserStatusAtSystemLevelCellRenderer
} from "admin-app/components/cellRenderer";
import CreateUser from "admin-app/components/create-user";
import UnlockUser from "admin-app/components/unlock-user";
import { getAccessToken, unlockUserAccountApi } from "services/auth";
import { Button, Modal } from "antd";
import { message } from "utils/ciq-message";
import { getCiqClientErrorMessage } from "utils/ciq-client-error";

function SubscriptionUsers({ subscription_id }: { subscription_id: string }) {
  const gridRef = useRef<AgGridReact<any>>(null);
  const [statsData, setStatsData] = useState<{
    displayCount: number;
    filterName: undefined | string;
  }>();
  const [createUserDrawer, setCreateUserDrawer] = useState<{
    showInviteModel: boolean;
    inviteUserData?: any;
  }>({
    showInviteModel: false,
    inviteUserData: null
  });
  const [unlockUserDrawer, setUnlockUserDrawer] = useState<{
    showUnlockModel: boolean;
    unlockUserData?: {
      email?: string;
      first_name?: string;
      last_name?: string;
      id?: string;
    } | null;
  }>({
    showUnlockModel: false,
    unlockUserData: null
  });

  const { query: qQuery, sub: sQuery } = getGQL(USERS_OF_SUBSCRIPTION);
  const { data: usersData } = useQuerySubscription<{
    subscription_users: Array<TSubscriptionUser>;
  }>(qQuery, sQuery, {
    variables: { subscription_id }
  });

  const handleUnlockUserSuccess = () => {
    setTimeout(() => {
      gridRef.current?.api?.refreshCells();
    }, 500);
  };

  const unlockUserAccount = async (email: string) => {
    try {
      const token = getAccessToken();
      if (!token) {
        message.error("Please login to unlock user");
        return;
      }
      const response = await unlockUserAccountApi({ email }, token);
      if (response.data) {
        message.success("User unlocked successfully");
        handleUnlockUserSuccess();
      } else {
        Modal.error({
          title: "Unable to unlock user",
          content: getCiqClientErrorMessage(response, "Failed to unlock user")
        });
      }
    } catch {
      Modal.error({
        title: "Unable to unlock user",
        content: "An error occurred while unlocking user"
      });
    }
  };

  const columnDefs: ColDef[] = useMemo<ColDef[]>(
    () => [
      dummyColumnDef,
      {
        headerName: "Serial No.",
        valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
        headerTooltip: "Serial No.",
        width: 100,
        sortable: false,
        editable: false,
        cellRenderer: truncatedTextCellRenderer
      },
      {
        colId: "name",
        headerName: "Name",
        headerTooltip: "Name",
        valueGetter: ({ data }) => {
          const first = data?.user?.first_name ?? "";
          const last = data?.user?.last_name ?? "";
          return `${first} ${last}`.trim();
        }
      },
      { colId: "email", field: "user.email", headerName: "Email" },
      {
        colId: "subscription_permission_id",
        field: "subscription_permission_id",
        headerName: "Permission",
        headerTooltip: "Permission",
        width: 150,
        valueGetter: ({ data }) => {
          const permissionId = data.subscription_permission_id;
          if (permissionId === 1) {
            return "Admin";
          }
          if (permissionId === 2) {
            return "Editor";
          }
          if (permissionId === 3) {
            return "Viewer";
          }
          return "None";
        }
      },
      {
        colId: "status_id",
        field: "status.id",
        headerName: "User Status in Subscription",
        headerTooltip: "User Status in Subscription",
        getQuickFilterText: ({ data }) => data.status.name,
        filterValueGetter: ({ data }) => data.status.name,
        cellRenderer: StatusCellRenderer
      },
      {
        headerName: "User Status in System",
        headerTooltip: "User Status in System",
        field: "user.status.id",
        colId: "user_status",
        initialWidth: 240,
        cellRenderer: UserStatusAtSystemLevelCellRenderer,
        getQuickFilterText: ({ data }) => data.user?.status?.name,
        filterValueGetter: ({ data }) => data.user?.status?.name
      },
      {
        colId: "created_by",
        field: "created_by_user.email",
        headerName: "Created By",
        headerTooltip: "Created By"
      },
      {
        colId: "created_at",
        field: "created_at",
        headerName: "Created at",
        headerTooltip: "Created at",
        initialWidth: 160,
        valueGetter: ({ data }) =>
          data.created_at ? DateUtils.format(data.created_at) : ""
      }
    ],
    []
  );
  const gridContext = {
    hasAdminAccess: true,
    setCreateUserDrawer,
    setUnlockUserDrawer,
    unlockUserAccount
  };

  const defaultColDef = useMemo(
    () =>
      ({
        sortable: true,
        editable: false,
        filter: true,
        resizable: true,
        comparator: comparatorWithEmptyAtBottom
      }) as ColDef,
    []
  );

  const onFiltersApplied = () => {
    setStatsData(
      calculateLogPageStats({
        gridRef
      })
    );
  };

  const statusBudge = useMemo(
    () =>
      getGridStatusBudge(
        {
          title: "Subscription Users",
          totalCount: usersData?.subscription_users?.length,
          filterStats: statsData
        },
        () => {
          gridRef.current?.api.resetColumnState();
        }
      ),
    [statsData, usersData?.subscription_users?.length]
  );

  return (
    <div className="h-[calc(100vh-180px)]">
      <div className="flex w-full justify-between items-center px-6 py-2">
        <div className="flex space-x-3 items-center">
          <div className="font-semibold text-base">Subscription Users</div>
          {statusBudge}
        </div>
        <div className="flex items-center gap-2">
          <CiqAgSearch
            placeholder="Search"
            gridRef={gridRef}
            data-testid="proj-feature-events-search"
          />
          <Button
            type="primary"
            data-testid="create-user-header-btn"
            onClick={() => {
              setCreateUserDrawer({
                showInviteModel: true,
                inviteUserData: {}
              });
            }}
          >
            Create User
          </Button>
        </div>
      </div>
      <CiqAgGridUtil
        gridRef={gridRef}
        rowData={usersData?.subscription_users}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        context={gridContext}
        onFilterChanged={onFiltersApplied}
      />
      {createUserDrawer.showInviteModel && (
        <CreateUser
          setDrawerOpen={setCreateUserDrawer}
          showDrawerOpen={createUserDrawer.showInviteModel}
          modelTitle="Create User"
          userData={createUserDrawer.inviteUserData}
          subscriptionId={subscription_id}
        />
      )}
      {unlockUserDrawer.showUnlockModel && (
        <UnlockUser
          setDrawerOpen={setUnlockUserDrawer}
          showDrawerOpen={unlockUserDrawer.showUnlockModel}
          modelTitle="Unlock User"
          userData={unlockUserDrawer.unlockUserData ?? {}}
          onSuccess={handleUnlockUserSuccess}
        />
      )}
    </div>
  );
}
export default SubscriptionUsers;
